
import React, { useMemo } from 'react';
import { ParkingSpace, ReleaseRecord } from '../types';
import { ICONS } from '../constants';

interface OwnerDashboardProps {
  spaces: ParkingSpace[];
  releases: ReleaseRecord[];
  onToggleRelease: (spaceId: number, date: string) => void;
  currentOwnerId: number;
  currentUserName: string;
}

const OwnerDashboard: React.FC<OwnerDashboardProps> = ({ 
  spaces, releases, onToggleRelease, currentOwnerId, currentUserName
}) => {
  const ownerSpace = spaces.find(s => s.id === currentOwnerId);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];
  
  const generateDates = (start: Date, count: number) => {
    const base = new Date(start);
    const day = base.getDay();
    const diff = base.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(base.setDate(diff));

    return Array.from({ length: count }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d.toISOString().split('T')[0];
    }).filter(d => {
      const dayOfWeek = new Date(d).getDay();
      return dayOfWeek !== 0 && dayOfWeek !== 6;
    });
  };

  const planningDates = generateDates(new Date(), 28).slice(0, 20);

  const DayCell: React.FC<{ date: string }> = ({ date }) => {
    if (!ownerSpace) return null;
    const release = releases.find(r => r.spaceId === ownerSpace.id && r.date === date);
    const isReleased = !!release;
    const isClaimed = release?.isClaimed;
    const claimantName = release?.claimedBy;
    const dateObj = new Date(date);
    const isToday = date === todayStr;
    const isPassed = date < todayStr;

    return (
      <div className={`p-4 rounded-xl border-2 flex flex-col justify-between h-36 transition-all ${
        isPassed ? 'opacity-30 grayscale bg-slate-50 border-slate-100 pointer-events-none' :
        isReleased 
          ? (isClaimed ? 'bg-emerald-50 border-emerald-500' : 'bg-amber-50 border-amber-500')
          : 'bg-white border-slate-200'
      } ${isToday && !isPassed ? 'ring-2 ring-accent-red border-accent-red' : ''}`}>
        <div className="flex justify-between items-start">
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
            {dateObj.toLocaleDateString(undefined, { weekday: 'short' })} {dateObj.getDate()}
          </span>
          {isToday && <span className="text-[8px] bg-accent-red text-white px-1.5 py-0.5 rounded font-black uppercase">Today</span>}
        </div>
        
        <div className="flex-1 flex flex-col justify-center py-2">
          <p className="text-black font-black text-xs uppercase truncate">
            {isReleased ? (isClaimed ? 'Assigned' : 'Released') : 'Reserved'}
          </p>
          {isClaimed && (
            <p className="text-[9px] font-bold text-emerald-700 bg-emerald-100/50 px-2 py-1 rounded mt-1 truncate">
              To: {claimantName}
            </p>
          )}
        </div>

        {!isPassed && (
          <button 
            onClick={() => !isClaimed && onToggleRelease(ownerSpace.id, date)}
            disabled={isClaimed}
            className={`w-full py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-colors ${
              isReleased ? 'bg-amber-500 text-white' : 'bg-accent-red text-white'
            } ${isClaimed ? 'opacity-30 cursor-not-allowed' : 'hover:bg-black'}`}
          >
            {isReleased ? 'Revoke' : 'Release'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-10 text-black">
      {ownerSpace ? (
        <section className="space-y-8">
          <div className="bg-brand-dark p-8 rounded-[1.5rem] text-white flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-accent-red rounded-xl flex items-center justify-center text-white shadow-lg"><ICONS.Car className="w-8 h-8" /></div>
              <div>
                <h2 className="text-2xl font-black tracking-tight">Designated Spot: {ownerSpace.displayLabel}</h2>
                <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest">{ownerSpace.zone} • Verified Member</p>
              </div>
            </div>
            <div className="text-right hidden md:block">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Ownership</p>
              <p className="text-lg font-black">{currentUserName}</p>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
            <div className="mb-6">
               <h3 className="text-sm font-black uppercase tracking-widest text-slate-400">Availability Planner (4 Weeks Mon-Fri)</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {planningDates.map(d => <DayCell key={d} date={d} />)}
            </div>
          </div>

          <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><ICONS.Check className="w-5 h-5 text-emerald-600" /></div>
            <p className="text-xs text-slate-600 font-medium">
              Passed days are automatically locked. Releasing spots up to 4 weeks ahead helps colleagues plan travel and rewards early booking.
            </p>
          </div>
        </section>
      ) : (
        <div className="bg-slate-50 p-20 rounded-[2rem] border-2 border-dashed border-slate-200 text-center">
          <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-slate-100">
            <ICONS.User className="w-10 h-10 text-slate-200" />
          </div>
          <h2 className="text-2xl font-black tracking-tight mb-2">Personnel Access Only</h2>
          <p className="text-slate-500 font-medium max-w-sm mx-auto">You do not have a designated parking space. Use the <strong>Access Requests</strong> tab to join the waiting list.</p>
        </div>
      )}
    </div>
  );
};

export default OwnerDashboard;
